import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Tooltip, useMediaQuery } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Copy01 } from './animate-ui/icons/hugeicons/copy-01';
import { LibraryBig } from './animate-ui/icons/library-big';
import FormWizard, {
  type FormWizardMethods,
  type FormWizardSchema,
  type WizardData,
} from 'react-form-wizard-component';
import 'react-form-wizard-component/dist/style.css';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import { Project } from '../contexts/ProjectContext';
import { useAppAppearance } from '../contexts/AppearanceContext';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import {
  emptySystemTemplateFilters,
  filterSystemTemplates,
  getSystemTemplateFilterOptions,
  getVisibleFilterKeys,
  metadataFilterKeys,
  type FilterKey,
  type Filters,
  type SystemTemplate,
} from '../utils/systemTemplates';
import { trackEvent } from '../statsig';
import ProjectCreationWaitModal from './ProjectCreationWaitModal';

type CreationSource = 'template' | 'project';

interface ProjectCreationWizardProps {
  open: boolean;
  projects: Project[];
  onClose: () => void;
  onProjectCreated: (project: Project) => void;
}

interface WizardValues extends WizardData {
  source?: CreationSource;
  selectedProjectId?: string;
  selectedTemplateId?: string;
  projectName?: string;
  filters?: Filters;
}

const filterLabels = (): Record<FilterKey, string> => ({
  production_type: t`Production type`,
  format_genre: t`Format / genre`,
  credit_type: t`Credit type`,
  language: t`Language`,
  territory: t`Territory`,
  broadcaster_platform: t`Broadcaster / platform`,
  fund_institution: t`Fund / institution`,
  bilingual: t`Bilingual`,
});

const filterOptionLabel = (key: FilterKey, option: string): string => {
  if (key !== 'bilingual') return option;
  return option === 'yes' ? t`Bilingual` : t`Not bilingual`;
};

const getValues = (data: WizardData): WizardValues => data as WizardValues;

const getFilters = (data: WizardData): Filters =>
  getValues(data).filters ?? emptySystemTemplateFilters();

const filterStepId = (key: FilterKey): string => `filter-${key}`;

const filterKeyFromStepId = (stepId: string): FilterKey | null => {
  if (!stepId.startsWith('filter-')) return null;
  return stepId.slice('filter-'.length) as FilterKey;
};

const templateFieldValue = (template: SystemTemplate, key: FilterKey): string => {
  if (key === 'bilingual') return template.bilingual ? t`Yes` : t`No`;
  return template[key].join(', ');
};

export default function ProjectCreationWizard({
  open,
  projects,
  onClose,
  onProjectCreated,
}: ProjectCreationWizardProps): React.ReactElement {
  useLingui();
  const { mode } = useAppAppearance();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const wizardRef = useRef<FormWizardMethods>(null);
  const templateLoadState = useRef<'idle' | 'loading' | 'loaded'>('idle');
  const [templates, setTemplates] = useState<SystemTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [creating, setCreating] = useState<boolean>(false);
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  const [data, setData] = useState<WizardValues>({
    filters: emptySystemTemplateFilters(),
  });
  const [activeStepId, setActiveStepId] = useState<string>('source');
  const [hoveredSource, setHoveredSource] = useState<CreationSource | null>(null);

  const fetchTemplates = useCallback(async (): Promise<void> => {
    if (templateLoadState.current !== 'idle') return;
    templateLoadState.current = 'loading';
    try {
      setLoading(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/system-templates`);
      const responseData = await response.json();
      if (responseData.status === 'OK') {
        setTemplates(responseData.templates ?? []);
        templateLoadState.current = 'loaded';
      } else {
        setError(responseData.message || t`Failed to load system templates`);
      }
    } catch (fetchError) {
      setError(t`Failed to load system templates. Please try again.`);
      console.error('Error fetching system templates:', fetchError);
    } finally {
      if (templateLoadState.current === 'loading') templateLoadState.current = 'idle';
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (open) void fetchTemplates();
  }, [fetchTemplates, open]);

  const reset = (): void => {
    setHoveredSource(null);
    setData({ filters: emptySystemTemplateFilters() });
    setError('');
    setActiveStepId('source');
    setExpandedTemplateId(null);
    wizardRef.current?.reset();
  };

  const handleClose = (): void => {
    if (creating) return;
    onClose();
  };

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.file_id === data.selectedTemplateId) ?? null,
    [data.selectedTemplateId, templates]
  );
  const filteredTemplates = useMemo(
    () => filterSystemTemplates(templates, getFilters(data)),
    [data, templates]
  );
  const visibleFilterKeys = useMemo(
    () => getVisibleFilterKeys(templates),
    [templates]
  );

  const updateData = useCallback((patch: Partial<WizardValues>): void => {
    setData((previous) => ({ ...previous, ...patch }));
  }, []);

  const setFilter = useCallback((key: FilterKey, value: string): void => {
    setData((previous) => {
      const selectedValues = getFilters(previous)[key];
      const nextValues = selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value];
      return {
        ...previous,
        filters: {
          ...getFilters(previous),
          [key]: nextValues,
        },
      };
    });
  }, []);

  const selectSource = useCallback((source: CreationSource): void => {
    setData((previous) => ({
      ...previous,
      source,
      selectedProjectId: source === 'template' ? undefined : previous.selectedProjectId,
      selectedTemplateId: source === 'project' ? undefined : previous.selectedTemplateId,
    }));
    const firstFilterKey = visibleFilterKeys[0];
    setActiveStepId(source === 'project' ? 'project' : firstFilterKey ? filterStepId(firstFilterKey) : 'template');
  }, [visibleFilterKeys]);

  const clearCurrentFilter = (): void => {
    const key = filterKeyFromStepId(activeStepId);
    if (!key) return;
    setData((previous) => ({
      ...previous,
      filters: {
        ...getFilters(previous),
        [key]: [],
      },
    }));
  };

  const handleCreate = async (): Promise<void> => {
    const projectName = data.projectName?.trim();
    if (!projectName || !data.source) return;

    const body = data.source === 'template'
      ? { name: projectName, templateSpreadsheetId: selectedTemplate?.file_id }
      : { name: projectName, sourceProjectId: data.selectedProjectId };
    if ((data.source === 'template' && !selectedTemplate) || (data.source === 'project' && !data.selectedProjectId)) return;

    try {
      setCreating(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const responseData = await response.json();
      if (responseData.status === 'OK') {
        trackEvent('project_created', data.source === 'template'
          ? {
              project_id: responseData.project?.id,
              project_name: responseData.project?.name,
              source_type: 'system_template',
              template_internal_key: selectedTemplate?.template_internal_key,
            }
          : {
              project_id: responseData.project?.id,
              project_name: responseData.project?.name,
              source_type: 'project',
              source_project_id: data.selectedProjectId,
            });
        onProjectCreated(responseData.project);
      } else {
        setError(responseData.message || t`Failed to create project`);
        setCreating(false);
      }
    } catch (createError) {
      setError(t`Failed to create project. Please try again.`);
      console.error('Error creating project:', createError);
      setCreating(false);
    }
  };

  const schema = useMemo<FormWizardSchema>(() => {
    const labels = filterLabels();
    return {
    initialData: { filters: emptySystemTemplateFilters() },
    steps: [
      ...visibleFilterKeys.map((key) => ({
        id: filterStepId(key),
        title: labels[key],
        condition: ({ data: wizardData }: { data: WizardData }) => getValues(wizardData).source === 'template',
        content: (
          <div className="project-wizard-options">
            <p>{t`Choose one or more options, or skip this question.`}</p>
            {getSystemTemplateFilterOptions(templates, key).map((option) => (
              <button key={option} type="button" aria-pressed={getFilters(data)[key].includes(option)} className={getFilters(data)[key].includes(option) ? 'selected' : ''} onClick={() => setFilter(key, option)}>
                {filterOptionLabel(key, option)}
              </button>
            ))}
          </div>
        ),
      })),
      {
        id: 'template',
        title: t`Suggested templates`,
        condition: ({ data: wizardData }) => getValues(wizardData).source === 'template',
        content: (
          <div className="project-wizard-selection">
            <div className="project-wizard-template-list">
              {filteredTemplates.length === 0 ? <p>{t`No templates match your answers. Go back to adjust or skip a question.`}</p> : filteredTemplates.map((template) => (
                <div key={template.file_id} className={`project-wizard-template-row ${data.selectedTemplateId === template.file_id ? 'selected' : ''}`}>
                  <button
                    type="button"
                    className="project-wizard-template-toggle"
                    aria-expanded={expandedTemplateId === template.file_id}
                    onClick={() => {
                      updateData({ selectedTemplateId: template.file_id });
                      setExpandedTemplateId((previous) => previous === template.file_id ? null : template.file_id);
                    }}
                  >
                    <span>
                      <strong>{template.display_name_english}</strong>
                      {template.template_description && <small>{template.template_description}</small>}
                    </span>
                    <ExpandMoreRoundedIcon aria-hidden="true" />
                  </button>
                  {expandedTemplateId === template.file_id && (
                    <div className="project-wizard-template-details">
                      {[...metadataFilterKeys, 'bilingual' as const].map((key) => {
                        const value = templateFieldValue(template, key);
                        if (!value) return null;
                        return (
                          <div key={key}>
                            <span>{labels[key]}</span>
                            <strong>{value}</strong>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ),
        validate: ({ data: wizardData }) => getValues(wizardData).selectedTemplateId ? true : t`Choose a template`,
      },
      {
        id: 'project',
        title: t`Existing project`,
        condition: ({ data: wizardData }) => getValues(wizardData).source === 'project',
        content: (
          <div className="project-wizard-selection">
            <div className="project-wizard-template-list">
              {projects.map((project) => (
                <button key={project.id} type="button" aria-pressed={data.selectedProjectId === project.id} className={data.selectedProjectId === project.id ? 'selected' : ''} onClick={() => updateData({ selectedProjectId: project.id })}>
                  <strong>{project.name}</strong>
                </button>
              ))}
            </div>
          </div>
        ),
        validate: ({ data: wizardData }) => getValues(wizardData).selectedProjectId ? true : t`Choose a project`,
      },
      {
        id: 'name',
        title: t`Project name`,
        content: (
          <>
            <label className="project-dialog-field-label" htmlFor="project-wizard-name">{t`Project name`}</label>
            <input
              autoFocus
              id="project-wizard-name"
              type="text"
              value={data.projectName ?? ''}
              onChange={(event) => updateData({ projectName: event.target.value })}
              className="project-name-input project-wizard-project-name"
            />
          </>
        ),
        validate: ({ data: wizardData }) => getValues(wizardData).projectName?.trim() ? true : t`Enter a project name`,
      },
    ],
    };
  }, [data, expandedTemplateId, filteredTemplates, projects, setFilter, templates, updateData, visibleFilterKeys]);

  const activeFilterKey = filterKeyFromStepId(activeStepId);
  const currentSelectionExists = activeFilterKey ? getFilters(data)[activeFilterKey].length > 0 : true;
  const isTemplateFlow = data.source === 'template';
  const activeSteps = schema.steps.filter((step) => isTemplateFlow
    ? step.id !== 'project'
    : step.id === 'project' || step.id === 'name');
  const stepCount = activeSteps.length;
  const currentStep = Math.max(0, activeSteps.findIndex((step) => step.id === activeStepId)) + 1;
  const stepLabel = t`Step ${currentStep} of ${stepCount}`;
  const projectNameMissing = !data.projectName?.trim();

  return (
    <>
      <Dialog
        open={open && !creating}
        onClose={handleClose}
        transitionDuration={reducedMotion ? 0 : undefined}
        slotProps={{ transition: { onExited: () => { if (!creating) reset(); } } }}
        aria-labelledby="project-wizard-title"
        className="project-creation-dialog project-wizard-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span id="project-wizard-title">{t`Start a new project`}</span>
          <Button onClick={handleClose} disabled={creating}>{t`Cancel`}</Button>
        </DialogTitle>
        <DialogContent>
          {loading && data.source === 'template' ? <p className="project-dialog-description">{t`Loading system templates...`}</p> : (
            <>
              {error && <div className="error-message">{error}</div>}
              {!data.source ? (
                <div className="project-wizard-options project-wizard-sources">
                  <p>{t`Choose how you want to start your project.`}</p>
                  <button
                    type="button"
                    disabled={loading}
                    onMouseEnter={() => setHoveredSource('template')}
                    onMouseLeave={() => setHoveredSource(null)}
                    onClick={() => selectSource('template')}
                  >
                    <span className="project-wizard-source-icon" aria-hidden="true">
                      <LibraryBig size={28} animate={!reducedMotion && !loading && hoveredSource === 'template'} />
                    </span>
                    <span>{loading ? t`Loading system templates...` : t`Use a system template`}</span>
                    <ArrowForwardRoundedIcon className="project-wizard-source-arrow" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onMouseEnter={() => setHoveredSource('project')}
                    onMouseLeave={() => setHoveredSource(null)}
                    onClick={() => selectSource('project')}
                  >
                    <span className="project-wizard-source-icon" aria-hidden="true">
                      <Copy01 size={28} animate={!reducedMotion && hoveredSource === 'project'} />
                    </span>
                    <span>{t`Copy an existing project`}</span>
                    <ArrowForwardRoundedIcon className="project-wizard-source-arrow" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <FormWizard
                  ref={wizardRef}
                  schema={schema}
                  data={data}
                  onDataChange={(nextData) => setData(getValues(nextData))}
                  onTabChange={(event) => setActiveStepId(event.stepId ?? '')}
                  onComplete={() => { void handleCreate(); }}
                  colorScheme={mode}
                  title={
                    <>
                      <h3 className="project-wizard-question">{schema.steps.find((step) => step.id === activeStepId)?.title}</h3>
                      <div className="project-wizard-progress">
                        <span id="project-wizard-progress-label">{stepLabel}</span>
                        <div
                          className="project-wizard-progress-track"
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuenow={currentStep}
                          aria-valuemax={stepCount}
                          aria-labelledby="project-wizard-progress-label"
                        >
                          <span className="project-wizard-progress-fill" style={{ transform: `scaleX(${currentStep / stepCount})` }} />
                        </div>
                      </div>
                    </>
                  }
                  theme={{
                    primaryColor: 'var(--selector-text)',
                    backgroundColor: 'transparent',
                    textColor: 'var(--selector-text)',
                    tabColor: 'var(--selector-surface-raised)',
                    tabIconColor: 'var(--selector-muted)',
                    borderColor: 'var(--selector-rule)',
                  }}
                  ariaLabel={t`New project wizard`}
                  nextButtonTemplate={(next) => (
                    <div className="project-wizard-next-actions">
                      {isTemplateFlow && activeStepId !== 'name' && (
                        <span className="project-wizard-match-count">{t`${filteredTemplates.length} template matches found`}</span>
                      )}
                      {activeFilterKey && <Button onClick={() => { clearCurrentFilter(); next(); }}>{t`Skip`}</Button>}
                      <Button variant="contained" onClick={next} disabled={activeFilterKey !== null && !currentSelectionExists}>{t`Next`}</Button>
                    </div>
                  )}
                  backButtonTemplate={(back) => <Button className="project-wizard-back-button" onClick={back}>{t`Back`}</Button>}
                  finishButtonTemplate={(finish) => (
                    <Tooltip title={projectNameMissing ? t`You must give a name to the project` : ''}>
                      <span>
                        <Button variant="contained" onClick={finish} disabled={projectNameMissing}>
                          {t`Create Project`}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ProjectCreationWaitModal open={creating} />
    </>
  );
}
